import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  VStack,
} from "@chakra-ui/react";
import { type IOption, Select } from "@components/ui-kit";
import { Trans } from "@lingui/macro";
import {
  type ExternalLink,
  ExternalLinkType,
} from "@src/__generated__/urql-graphql";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import { forwardRef, useImperativeHandle } from "react";
import { getExternalLinksRelatesToOptions } from "../../MixedFilters";
import type { StoreDocuments } from "../../StoreDocuments";

type TForm = Pick<ExternalLink, "link" | "title"> &
  Partial<Pick<ExternalLink, "model_id" | "model_type">>;

type TProps = {
  initState?: Partial<TForm>;
  projectOptions?: IOption[];
  brandOptions?: IOption[];
  type: StoreDocuments["extLinkType"];
  onSubmit: (form: Required<TForm>) => void;
};

export type TExtLinkCreateFormRef = {
  onSubmit: () => void;
};

export const ExtLinkCreateForm = observer(
  forwardRef<TExtLinkCreateFormRef, TProps>(function ExtLinkCreateForm(
    { initState, onSubmit, type, projectOptions, brandOptions },
    ref,
  ) {
    const { query } = useRouter();
    const state = useLocalObservable(() => ({
      link: new FieldState(initState?.link)
        .validators(required)
        .enableAutoValidation(),
      title: new FieldState(initState?.title)
        .validators(required)
        .enableAutoValidation(),
      model_id: new FieldState<string | undefined>(
        initState?.model_id ?? (query.id as string),
      )
        .validators(required)
        .enableAutoValidation(),
      model_type: new FieldState<ExternalLinkType | undefined>(
        initState?.model_type ?? (type && type !== "all" ? type : undefined),
      )
        .validators(required)
        .enableAutoValidation(),
    }));

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          handleSubmit();
        },
      }),
      [state.link, state.title],
    );

    const handleSubmit = async () => {
      const form = new FormState([
        state.link,
        state.title,
        state.model_id,
        state.model_type,
      ]);

      const res = await form.validate();

      if (
        res.hasError ||
        !state.link.$ ||
        !state.title.$ ||
        !state.model_id.$ ||
        !state.model_type.$
      )
        return;

      onSubmit({
        link: state.link.$,
        title: state.title.$,
        model_id: state.model_id.$,
        model_type: state.model_type.$,
      });
    };

    return (
      <VStack spacing="4">
        <FormControl isInvalid={state.title.hasError}>
          <FormLabel>
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            onChange={({ target }) => {
              state.title.onChange(target.value);
            }}
            value={state.title.value}
          />
          <FormErrorMessage>{state.title.error}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={state.link.hasError}>
          <FormLabel>
            <Trans>Link</Trans>
          </FormLabel>
          <Input
            onChange={({ target }) => {
              state.link.onChange(target.value);
            }}
            value={state.link.value}
          />
          <FormErrorMessage>{state.link.error}</FormErrorMessage>
        </FormControl>
        {(!type || type === "all") && (
          <HStack align="start" w="full">
            <FormControl isInvalid={state.model_type.hasError}>
              <FormLabel>
                <Trans>Relates to</Trans>
              </FormLabel>
              <Select
                {...fieldToSelectProps(
                  state.model_type,
                  getExternalLinksRelatesToOptions(),
                )}
              />
              <FormErrorMessage>{state.model_type.error}</FormErrorMessage>
            </FormControl>
            {state.model_type.$ && (
              <FormControl isInvalid={state.model_id.hasError}>
                <FormLabel>
                  {state.model_type.$ === ExternalLinkType.Project ? (
                    <Trans>Project</Trans>
                  ) : (
                    <Trans>Brand</Trans>
                  )}
                </FormLabel>
                <Select
                  {...fieldToSelectProps(
                    state.model_id,
                    state.model_type.$ === ExternalLinkType.Project
                      ? (projectOptions ?? [])
                      : (brandOptions ?? []),
                  )}
                />
                <FormErrorMessage>{state.model_id.error}</FormErrorMessage>
              </FormControl>
            )}
          </HStack>
        )}
      </VStack>
    );
  }),
);
